import clsx from 'clsx'
import { styled, SxProps, useForkRef } from '@mui/material'
import { ForwardedRef, forwardRef, RefObject, useEffect, useRef } from 'react'

export const HotSpot = forwardRef(function BeaconComponent(
  props: {
    'aria-label'?: string
    onClick?: () => unknown
    onMouseEnter?: () => unknown
    className?: string
    sx?: SxProps
    containerRef?: RefObject<HTMLElement>
    size?: 'small' | 'medium'
  },
  ref: ForwardedRef<HTMLSpanElement>,
) {
  const innerRef = useRef<HTMLSpanElement>(null)
  const handleRef = useForkRef(ref, innerRef)

  useEffect(() => {
    const container = props.containerRef?.current
    if (container) {
      const handleMouseEnter = () => {
        innerRef.current?.classList.add('pause-animation')
      }
      const handleMouseLeave = () => {
        innerRef.current?.classList.remove('pause-animation')
      }

      container.addEventListener('mouseenter', handleMouseEnter)
      container.addEventListener('mouseleave', handleMouseLeave)

      return () => {
        container.removeEventListener('mouseenter', handleMouseEnter)
        container.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [])

  return (
    <Root
      ref={handleRef}
      onClick={props.onClick}
      aria-label={props['aria-label']}
      onMouseEnter={props.onMouseEnter}
      className={clsx(props.className, props.size || 'medium')}
      sx={props.sx}
    >
      <Beacon className={props.size || 'medium'} />
    </Root>
  )
})

export const Root = styled('span')`
  animation: pulse 1.5s ease-in-out infinite;
  color: #ffa100;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 23px;
  width: 23px;
  cursor: pointer;
  border: 2px solid currentColor;
  box-sizing: border-box;
  box-shadow: ${({ theme }) => theme.shadows[2]};

  &.pause-animation {
    animation-play-state: paused;
  }

  &.small {
    height: 18px;
    width: 18px;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }

    10% {
      transform: scale(1);
    }

    55% {
      color: #ffb700;
      transform: scale(1.2);
    }
  }
`

const Beacon = styled('span')`
  background-color: currentColor;
  height: 15px;
  width: 15px;
  &.small {
    height: 10px;
    width: 10px;
  }
  border-radius: 50%;
`
